(() => {
  if (customElements.get('rdb-visual-navigation')) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  class NavCarousels extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.addEventListener('click', this.onRailClick, options);
      this.querySelectorAll('[data-nav-rail]').forEach(rail => rail.addEventListener('scroll', () => this.updateRail(rail), {...options, passive: true}));
      this.resize = new ResizeObserver(() => this.updateRails());
      this.querySelectorAll('[data-nav-rail]').forEach(rail => this.resize.observe(rail));
      this.updateRails();
    }
    disconnectedCallback() { this.controller?.abort(); this.resize?.disconnect(); }
    onRailClick = event => {
      const button = event.target.closest('[data-nav-scroll]');
      if (!button || button.disabled) return;
      const rail = this.querySelector('#' + CSS.escape(button.getAttribute('aria-controls')));
      if (!rail) return;
      const direction = getComputedStyle(rail).direction === 'rtl' ? -1 : 1;
      rail.scrollBy({left: direction * Number(button.dataset.navScroll) * (rail.clientWidth + 12), behavior: reduced.matches ? 'instant' : 'smooth'});
    };
    updateRail(rail) {
      const group = rail.closest('[data-nav-carousel]');
      if (!group) return;
      const max = rail.scrollWidth - rail.clientWidth, offset = Math.abs(rail.scrollLeft);
      const previous = group.querySelector('[data-nav-scroll="-1"]'), next = group.querySelector('[data-nav-scroll="1"]');
      if (previous) previous.disabled = offset <= 2;
      if (next) next.disabled = offset >= max - 2;
      group.querySelector('.rdb-nav-controls').hidden = max < 2;
    }
    updateRails() { this.querySelectorAll('[data-nav-rail]').forEach(rail => this.updateRail(rail)); }
  }

  class VisualNavigation extends NavCarousels {
    connectedCallback() {
      super.connectedCallback();
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-nav-open]');
      this.back = this.querySelector('[data-nav-back]');
      this.heading = this.querySelector('[data-nav-title]');
      this.scroller = this.querySelector('.rdb-nav-scroll');
      this.view = 'home'; this.epoch = 0; this.viewEpoch = 0;
      this.opener.hidden = false;
      const options = {signal: this.controller.signal};
      this.addEventListener('click', this.onClick, options);
      this.dialog.addEventListener('cancel', event => {event.preventDefault(); this.view === 'home' ? this.closeDrawer() : this.showView('home');}, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const items = [...this.dialog.querySelectorAll('a[href], button:enabled, input:enabled, select:enabled, textarea:enabled, [tabindex="0"]')].filter(item => item.checkVisibility());
        const first = items[0], last = items.at(-1);
        if (event.shiftKey && document.activeElement === first) {event.preventDefault(); last?.focus();}
        else if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first?.focus();}
      }, options);
      this.dialog.addEventListener('close', this.releaseLock, options);
      this.dialog.addEventListener('pointerdown', event => {this.backdropStart = event.target === this.dialog && this.outside(event);}, options);
      this.dialog.addEventListener('pointerup', event => {if (this.backdropStart && event.target === this.dialog && this.outside(event)) this.closeDrawer(); this.backdropStart = false;}, options);
      this.desktop = matchMedia('(min-width: 1440px)');
      this.desktop.addEventListener('change', () => {if (this.desktop.matches) this.closeDrawer(true);}, options);
      this.opener.setAttribute('aria-expanded', 'false');
    }
    disconnectedCallback() { this.closeDrawer(true); super.disconnectedCallback(); }
    outside(event) { const r = this.dialog.getBoundingClientRect(); return event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom; }
    onClick = event => {
      if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.target.closest('[data-nav-open]')) { this.openDrawer(); return; }
      if (event.target.closest('[data-nav-close]')) { this.closeDrawer(); return; }
      if (event.target.closest('[data-nav-back]')) { this.showView('home'); return; }
      const link = event.target.closest('[data-nav-target]');
      if (link && this.querySelector('[data-nav-view="' + CSS.escape(link.dataset.navTarget) + '"]')) {
        event.preventDefault(); this.returnTo = link; this.showView(link.dataset.navTarget);
      }
    };
    openDrawer() {
      const epoch = ++this.epoch;
      clearTimeout(this.closeTimer);
      if (!this.dialog.open) {
        this.hadScrollLock = document.documentElement.hasAttribute('scroll-lock');
        document.documentElement.setAttribute('scroll-lock', '');
        document.documentElement.setAttribute('data-rdb-nav-lock', '');
        this.dialog.showModal();
      }
      this.opener.setAttribute('aria-expanded', 'true');
      this.dialog.getBoundingClientRect();
      requestAnimationFrame(() => {if (epoch === this.epoch && this.dialog.open) this.dialog.dataset.open = '';});
      this.querySelector('[data-nav-close]').focus({preventScroll: true});
      this.updateRails();
    }
    closeDrawer(immediate = false) {
      if (!this.dialog?.open) return;
      const epoch = ++this.epoch;
      clearTimeout(this.closeTimer);
      this.dialog.removeAttribute('data-open');
      this.opener.setAttribute('aria-expanded', 'false');
      const finish = () => {
        if (epoch !== this.epoch) return;
        this.dialog.close(); this.releaseLock();
        ++this.viewEpoch; this.transition?.cancel();
        this.querySelectorAll('[data-nav-view]').forEach(view => {view.hidden = view.dataset.navView !== 'home'; view.inert = false;});
        this.view = 'home'; this.back.hidden = true;
        this.heading.textContent = this.heading.dataset.homeTitle;
        this.scroller.scrollTop = 0;
        if (this.opener.checkVisibility()) this.opener.focus({preventScroll: true});
      };
      if (immediate || reduced.matches) finish(); else this.closeTimer = setTimeout(finish, 240);
    }
    releaseLock = () => {
      if (this.dialog?.open) return;
      document.documentElement.removeAttribute('data-rdb-nav-lock');
      if (!this.hadScrollLock && !document.querySelector('dialog[open][scroll-lock]')) document.documentElement.removeAttribute('scroll-lock');
    };
    async showView(name) {
      if (this.view === name || !this.dialog.open) return;
      const target = this.querySelector('[data-nav-view="' + CSS.escape(name) + '"]');
      if (!target) return;
      const epoch = ++this.viewEpoch, outgoing = this.querySelector('[data-nav-view]:not([hidden])');
      this.transition?.cancel();
      if (this.view === 'home') this.homeScroll = this.scroller.scrollTop;
      if (!reduced.matches && outgoing) {
        this.transition = outgoing.animate([{opacity: 1}, {opacity: 0}], {duration: 90, easing: 'ease-out'});
        await this.transition.finished.catch(() => {});
        if (epoch !== this.viewEpoch || !this.dialog.open) return;
      }
      this.querySelectorAll('[data-nav-view]').forEach(view => {view.hidden = view !== target;});
      this.view = name; this.back.hidden = name === 'home';
      this.heading.textContent = name === 'home' ? this.heading.dataset.homeTitle : target.dataset.navLabel;
      this.scroller.scrollTop = name === 'home' ? this.homeScroll || 0 : 0;
      if (!reduced.matches) this.transition = target.animate([{opacity: 0, transform: 'translateY(5px)'}, {opacity: 1, transform: 'none'}], {duration: 160, easing: 'cubic-bezier(.16,1,.3,1)'});
      const focus = name === 'home' ? this.returnTo || this.querySelector('[data-nav-close]') : this.back;
      focus.focus({preventScroll: true}); this.updateRails();
    }
  }
  customElements.define('rdb-nav-carousels', NavCarousels);
  customElements.define('rdb-visual-navigation', VisualNavigation);
})();
